import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePlatformStore } from "@platform/store";
import { validateOrgInvitation } from "@platform/utils/validateOrgInvitation";

export function useRedirectWhenNoInvitation() {
  const isHydrated = usePlatformStore((s) => s.isHydrated);
  const setInvitation = usePlatformStore((s) => s.setInvitation);
  const { shortcode } = useParams();
  const navigate = useNavigate();
  const [isValidated, setIsValidated] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const hasValidatedRef = useRef(false);

  useEffect(() => {
    // Prevent re-validation
    if (hasValidatedRef.current) return;

    // Wait until the platform is hydrated with local storage
    if (!isHydrated) return;

    // If no shortcode in URL, redirect to home
    if (!shortcode) {
      navigate("/", { replace: true });
      return;
    }

    // Mark as validating to prevent duplicate calls
    hasValidatedRef.current = true;

    // Validate the invitation code from URL
    validateOrgInvitation({ shortcode, type: "shortcode" })
      .then((validatedInvitation) => {
        // Store the validated invitation
        setInvitation(validatedInvitation, "direct_link");
        setIsValidating(false);
        setIsValidated(true);
      })
      .catch((error: Error) => {
        const message = error.message || "";
        const isUnknownCode =
          message === "Invalid invitation code" ||
          message === "Template invitations cannot be claimed directly";

        setIsValidating(false);

        // Unknown/template codes go to homepage; actionable errors
        // (expired, inactive, max uses) redirect to marketing page with inline error
        navigate(
          isUnknownCode ? "/" : `/invitation/${shortcode}`,
          { replace: true }
        );
      });
  }, [isHydrated, navigate, shortcode, setInvitation]);

  return { isValidating, isValidated };
}

export default useRedirectWhenNoInvitation;
